import type { CSSProperties } from "react";

interface SkeletonProps {
	width?: number | string;
	height?: number | string;
	radius?: number | string;
	style?: CSSProperties;
}

/**
 * Shimmering placeholder block. Use to avoid empty-flash while data is loading.
 */
export function Skeleton({ width = "100%", height = 14, radius = 4, style }: SkeletonProps) {
	return (
		<div
			aria-hidden="true"
			style={{
				width,
				height,
				borderRadius: radius,
				background:
					"linear-gradient(90deg, var(--bg-tertiary) 0%, var(--bg-hover) 50%, var(--bg-tertiary) 100%)",
				backgroundSize: "200% 100%",
				animation: "skeletonShimmer 1.4s linear infinite",
				...style,
			}}
		/>
	);
}

/**
 * Skeleton approximation of a sidebar card — status dot, title, meta line.
 */
export function SkeletonCard() {
	return (
		<div
			style={{
				padding: "10px 14px",
				background: "var(--bg-secondary)",
				border: "1px solid var(--border)",
				borderRadius: 8,
				display: "flex",
				flexDirection: "column",
				gap: 6,
			}}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Skeleton width={8} height={8} radius="50%" />
				<Skeleton width="60%" height={13} />
			</div>
			<Skeleton width="40%" height={10} />
		</div>
	);
}

/**
 * Vertical stack of skeleton cards. Pass `count` for the number of placeholders.
 */
export function SkeletonCardList({ count = 4 }: { count?: number }) {
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			{Array.from({ length: count }).map((_, i) => (
				<SkeletonCard key={i} />
			))}
		</div>
	);
}
